import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Copy, Archive, Globe, Sparkles, Trash2 } from 'lucide-react';
import api from '../../services/api';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import alertService from '../../utils/alert';
import TemplateGalleryModal from './TemplateGalleryModal';

const STATUS_BADGE = { draft: 'slate', published: 'success', archived: 'secondary' };

export default function FormsListPage() {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/forms');
      setForms(data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createForm = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/forms', { name: name.trim() });
      setShowCreate(false);
      setName('');
      navigate(`/dashboard/forms/${data.data.form._id}`);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to create form');
    } finally {
      setCreating(false);
    }
  };

  const duplicateForm = async (form) => {
    try {
      const { data } = await api.post(`/forms/${form._id}/duplicate`);
      alertService.toast('success', `Duplicated as "${data.data.form.name}"`);
      load();
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to duplicate form');
    }
  };

  const archiveForm = async (form) => {
    if (!(await alertService.confirm('Archive?', `Archive "${form.name}"? It will stop accepting submissions.`, 'danger', 'Archive'))) return;
    try {
      await api.post(`/forms/${form._id}/archive`);
      load();
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to archive form');
    }
  };

  // Archive only ever hides a form from accepting new submissions — there
  // was no way to actually remove one (a test/duplicate created by mistake,
  // say) short of an engineer deleting it directly in Mongo.
  const deleteForm = async (form) => {
    const confirmed = await alertService.confirm(
      'Delete Form?',
      `Permanently delete "${form.name}" and all of its submissions? This cannot be undone.`,
      'danger',
      'Delete'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/forms/${form._id}`);
      alertService.toast('success', `"${form.name}" deleted.`);
      load();
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to delete form');
    }
  };

  const fetchTemplates = async () => {
    const { data } = await api.get('/forms/templates');
    return {
      builtIn: (data.data.builtIn || []).map((t) => ({ ...t, source: 'built-in' })),
      own: (data.data.own || []).map((t) => ({ ...t, source: 'own' })),
    };
  };

  const createFromTemplate = async ({ builtInKey, templateId, name: templateName }) => {
    try {
      const { data } = await api.post('/forms/from-template', { builtInKey, templateId, name: templateName });
      setShowTemplates(false);
      navigate(`/dashboard/forms/${data.data.form._id}`);
    } catch (err) {
      alertService.toast('error', err.response?.data?.message || 'Failed to create form from template');
    }
  };

  return (
    <DashboardModuleShell
      title="Forms"
      subtitle="Build schema-driven intake, capture, and application forms."
      icon={FileText}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" icon={Sparkles} onClick={() => setShowTemplates(true)}>New from Template</Button>
          <Button variant="brand" icon={Plus} onClick={() => setShowCreate(true)}>New Form</Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center h-64 text-[#706E6B] text-[13px]">Loading…</div>
      ) : forms.length === 0 ? (
        <Card className="py-16 text-center">
          <FileText size={40} className="mx-auto mb-3 text-[#D8DDE6]" />
          <p className="font-bold text-[#080707]">No forms yet</p>
          <p className="text-[12px] text-[#706E6B] mt-1">Create your first form to start collecting submissions.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {forms.map((form) => (
            <Card key={form._id} className="p-4 flex items-center justify-between gap-4">
              <button
                className="flex-1 min-w-0 text-left"
                onClick={() => navigate(`/dashboard/forms/${form._id}`)}
              >
                <div className="flex items-center gap-2">
                  <p className="font-bold text-[13px] text-[#080707] truncate">{form.name}</p>
                  <Badge variant={STATUS_BADGE[form.status]}>{form.status}</Badge>
                </div>
                {form.description && <p className="text-[11px] text-[#706E6B] mt-0.5 truncate">{form.description}</p>}
              </button>
              <div className="flex items-center gap-1 shrink-0">
                {form.status === 'published' && (
                  <a
                    href={`/forms/${form._id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] rounded-[4px] transition-colors"
                    title="Open public link"
                  >
                    <Globe size={15} />
                  </a>
                )}
                <button onClick={() => duplicateForm(form)} className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F4F6F9] rounded-[4px] transition-colors" title="Duplicate">
                  <Copy size={15} />
                </button>
                {form.status !== 'archived' && (
                  <button onClick={() => archiveForm(form)} className="p-2 text-[#706E6B] hover:text-[#DD7A01] hover:bg-[#FFF4E5] rounded-[4px] transition-colors" title="Archive">
                    <Archive size={15} />
                  </button>
                )}
                <button onClick={() => deleteForm(form)} className="p-2 text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FDE7E9] rounded-[4px] transition-colors" title="Delete">
                  <Trash2 size={15} />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Form"
        icon={FileText}
        maxWidth="max-w-md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button variant="brand" loading={creating} disabled={!name.trim()} onClick={createForm}>Create</Button>
          </>
        }
      >
        <Input
          label="Form Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Customer Intake"
          onKeyDown={(e) => e.key === 'Enter' && createForm()}
        />
      </Modal>

      {showTemplates && (
        <TemplateGalleryModal
          onClose={() => setShowTemplates(false)}
          onFetchTemplates={fetchTemplates}
          onCreateFromTemplate={createFromTemplate}
        />
      )}
    </DashboardModuleShell>
  );
}
